import React, { useState } from "react";
import dayjs from "dayjs";
import { Button, DatePicker, Radio } from "antd";
import { useDispatch, useSelector } from "react-redux";

import {
  setDateFilterType,
  setLeadDateRange,
  setLeadDateType,
} from "../../../../redux/slices/leadSlice";
import { CUSTOM, DATE_FILTER_OPTIONS } from "../../../commonComponents/DateRangeFilter";
import {
  OPPORTUNITY_DATE_BASIS,
  OPPORTUNITY_DATE_BASIS_OPTIONS,
} from "../../../commonComponents/OpportunityDateBasisDropdown";

const DateRangeFiltersContent = ({
  variables,
  setVariables,
  scrollListToTop,
  clearUrl,
  onFilterChange,
  onClear,
}) => {
  const dispatch = useDispatch();
  const dateFilterType = useSelector(state => state.lead.filters.dateFilterType || null);
  const dateRangeISO = useSelector(state => state.lead.filters.dateRange || []);
  const dateType = useSelector(
    state => state.lead.filters.dateType || OPPORTUNITY_DATE_BASIS.UPDATED_AT
  );

  const handleDateTypeChange = nextValue => {
    dispatch(setLeadDateType(nextValue));
    // Always re-fire — Apollo refetches on variable change, and the
    // page/scroll reset should run so the result list is consistent.
    setVariables?.({ ...variables, page: 1 });
    scrollListToTop?.();
    clearUrl?.();
    onFilterChange?.();
  };

  // Picker-only state for the CUSTOM intermediate step: when the user picks
  // "Custom Date" from the dropdown we keep the picker showing CUSTOM but
  // DON'T dispatch yet — only after both start + end dates are picked do
  // we commit. Avoids firing the leads query with filterType=CUSTOM and
  // an empty dateRange.
  const [pendingCustom, setPendingCustom] = useState(false);

  // Picker value source of truth: while we have a committed CUSTOM with a
  // populated range, rehydrate from redux; otherwise use the local pending
  // flag to render the dropdown as CUSTOM until the user finishes picking.
  const effectiveFilterType = pendingCustom ? CUSTOM : dateFilterType;

  // The shared DateRangeFilter component renders the custom picker using
  // dayjs objects, so rehydrate from the stored ISO pair on every render.
  const dateRangePickerValue =
    Array.isArray(dateRangeISO) && dateRangeISO.length === 2
      ? [dayjs(dateRangeISO[0]), dayjs(dateRangeISO[1])]
      : null;

  const handleDateFilterChange = nextValue => {
    if (nextValue === CUSTOM) {
      // Only reveal the date picker — DON'T touch redux, so the leads
      // query doesn't fire until the user picks both ends. The redux
      // dateFilterType keeps whatever was committed previously; UI shows
      // CUSTOM selected via `pendingCustom`.
      setPendingCustom(true);
      return;
    }
    // Preset or "no filter" — commit immediately.
    setPendingCustom(false);
    setVariables?.({ ...variables, page: 1 });
    dispatch(setDateFilterType(nextValue || ""));
    dispatch(setLeadDateRange([]));
    scrollListToTop?.();
    clearUrl?.();
    onFilterChange?.();
  };

  const handleDateRangeChange = nextRange => {
    if (Array.isArray(nextRange) && nextRange[0] && nextRange[1]) {
      // Both dates picked → commit CUSTOM + the range together so the
      // query fires exactly once with a valid payload.
      setPendingCustom(false);
      setVariables?.({ ...variables, page: 1 });
      dispatch(setDateFilterType(CUSTOM));
      dispatch(
        setLeadDateRange([
          dayjs(nextRange[0]).startOf("day").toISOString(),
          dayjs(nextRange[1]).endOf("day").toISOString(),
        ])
      );
      scrollListToTop?.();
      clearUrl?.();
      onFilterChange?.();
      return;
    }
    // User cleared / partially picked — keep the picker open in pending
    // state and clear the previously-committed CUSTOM range. Keep
    // `dateFilterType = CUSTOM` in redux so the parent's skip guard
    // (CUSTOM + incomplete range → skip) holds the query, matching
    // what the UI still shows (Custom radio selected, no range). Firing
    // with `dateFilterType = ""` here would look like "no filter" and
    // return the whole list — not what the user intends.
    setPendingCustom(true);
    if (dateFilterType === CUSTOM || dateRangeISO.length === 2) {
      setVariables?.({ ...variables, page: 1 });
      dispatch(setDateFilterType(CUSTOM));
      dispatch(setLeadDateRange([]));
      scrollListToTop?.();
      clearUrl?.();
      onFilterChange?.();
    }
  };

  const hasSelection =
    !!dateFilterType || (Array.isArray(dateRangeISO) && dateRangeISO.length === 2);

  return (
    <div className="flex flex-col px-3 py-2 gap-2">
      {onClear && hasSelection && (
        <div className="flex justify-end">
          <Button type="link" size="small" className="!p-0 !h-auto" onClick={onClear}>
            Clear
          </Button>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-black-65">Date type</span>
        <Radio.Group
          value={dateType}
          onChange={e => handleDateTypeChange(e.target.value)}
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          {OPPORTUNITY_DATE_BASIS_OPTIONS.map(opt => (
            <Radio key={opt.key} value={opt.key}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>

      <div className="flex flex-col gap-1 mt-2">
        <span className="text-xs text-black-65">Date range</span>
        {/* Vertical radio list of presets + Custom. Picking a preset commits
            immediately; picking Custom reveals the range picker below and
            only commits once both ends are chosen. */}
        <Radio.Group
          value={effectiveFilterType || undefined}
          onChange={e => handleDateFilterChange(e.target.value)}
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          {DATE_FILTER_OPTIONS.map(opt => (
            <Radio key={opt.value} value={opt.value}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
        {effectiveFilterType === CUSTOM && (
          <DatePicker.RangePicker
            className="mt-1 ml-auto"
            style={{ width: 240 }}
            value={dateRangePickerValue}
            onChange={handleDateRangeChange}
          />
        )}
      </div>
    </div>
  );
};

export default DateRangeFiltersContent;
